import { HTMLProps, PropsWithChildren } from "react";
import { useTeamId } from "stores/ServerInteractionStore";

import "./Material.css";

function simpleHash(salt: string, seed: string): number {
  const str = salt + seed;
  let hash = 0;
  for (let i = 0; i < str.length; ++i)
    hash = (hash << 5) - hash + str.charCodeAt(i);
  return hash >>> 0;
}

type MaterialProps = PropsWithChildren<{
  rotate?: number;
}> &
  // Enforce the following: if there is a single string child, it can
  // automatically be used as a seed. If children is any other type then
  // seed must be specified. The type intersection means the any is fine;
  // it just resolves to whatever PropsWithChildren.children is.
  (| {
        seed?: string;
        children: string;
      }
    | {
        seed: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        children: any;
      }
  ) &
  HTMLProps<HTMLDivElement>;

export function Paper({
  rotate,
  seed,
  children,
  style = {},
  className = "",
  ...otherProps
}: MaterialProps) {
  const hash = simpleHash(useTeamId() ?? "", seed ?? children);
  return (
    <div
      className={`paper ${className}`}
      style={{
        ...style,
        transform:
          rotate === undefined
            ? undefined
            : `rotate(${hash / (1 << 30) - 2 + rotate}deg)`,
      }}
      {...otherProps}
    >
      <div
        className="paper-inner"
        style={{
          maskPosition: `${(hash & 0x55555555) % 100}% ${((hash >>> 1) & 0x55555555) % 100}%`,
        }}
      >
        <div
          className="paper-before"
          style={{
            filter: `hue-rotate(${hash % 360}deg)`,
            borderImageSource: `url(/static/images/paper-edge${hash % 8}.png)`,
          }}
        />
        {children}
      </div>
    </div>
  );
}

export function Metal({
  rotate,
  seed,
  active = false,
  children,
  style = {},
  className = "",
  ...otherProps
}: MaterialProps & { active?: boolean }) {
  const hash = simpleHash(useTeamId() ?? "", seed ?? children);
  return (
    <div
      className={`metal ${className} ${active ? "active" : ""}`}
      style={{
        ...style,
        transform:
          rotate === undefined
            ? undefined
            : `rotate(${hash / (1 << 30) - 2 + rotate}deg)`,
      }}
      {...otherProps}
    >
      <div
        className="metal-inner"
        style={{
          maskPosition: `${(hash & 0x55555555) % 100}% ${((hash >>> 1) & 0x55555555) % 100}%`,
        }}
      >
        {children}
      </div>
    </div>
  );
}

export function Fabric({
  seed,
  backgroundImage,
  className = "",
  ...otherProps
}: {
  seed: string;
  backgroundImage?: string;
} & HTMLProps<HTMLDivElement>) {
  const hash = simpleHash(useTeamId() ?? "", seed);
  return (
    <div className={`fabric ${className}`} {...otherProps}>
      <div className="fabric-inner" style={{ backgroundImage }} />
      <div
        className="fabric-over"
        style={{
          backgroundPosition: `${(hash & 0x55555555) % 100}% ${((hash >>> 1) & 0x55555555) % 100}%`,
        }}
      />
    </div>
  );
}
